import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAllUsers, updateUser } from '../../services/userService';

const ADMIN_SESSION_KEY = 'vf_admin_session';

const checkAdminSession = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';
};

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to everything' },
  { value: 'editor', label: 'Editor', description: 'Can upload media and manage albums' },
  { value: 'creator', label: 'Creator', description: 'Album owner - can manage their album' },
  { value: 'invitee', label: 'Invitee', description: 'View access only' },
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AdminUsers] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Users</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reload Page</button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AdminUsers() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(false);

  useEffect(() => {
    try {
      const isAuth = checkAdminSession();
      if (isAuth) {
        setIsAuthenticated(true);
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      console.error('[AdminUsers] Auth check failed:', err);
      setError(err.message);
      navigate('/admin', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    setFetching(true);
    setError(null);
    
    try {
      const userList = await getAllUsers({ limit: 200 });
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
    }
  }, [isAuthenticated, fetchUsers]);

  useEffect(() => {
    let filtered = users;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(term) ||
        u.phone?.includes(term) ||
        u.uid?.toLowerCase().includes(term)
      );
    }
    
    if (roleFilter) {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    navigate('/admin', { replace: true });
  };

  const handleUpdateRole = async (uid, newRole) => {
    try {
      await updateUser(uid, { role: newRole });
      
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, role: newRole } : u));
      setSelectedUser(null);
      setEditingUser(false);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return '#8b5cf6';
      case 'editor': return '#3b82f6';
      case 'creator': return '#f59e0b';
      case 'invitee': return '#6b7280';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p style={{ color: '#666' }}>Redirecting to login...</p>
      </main>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'media', label: 'Media Upload', path: '/admin/media' },
    { id: 'invites', label: 'Invites', path: '/admin/invites' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'albums', label: 'Albums', path: '/admin/albums' },
    { id: 'settings', label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <ErrorBoundary>
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff' }}>
        {error && (
          <div style={{ background: '#7f1d1d', padding: '1rem', borderBottom: '1px solid #ef4444', textAlign: 'center' }}>
            <p style={{ color: '#fecaca', margin: 0 }}>⚠️ {error}</p>
          </div>
        )}
        
        <header style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
              </Link>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Admin</span>
            </div>
            <button onClick={handleLogout} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #444', background: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer' }}>Logout</button>
          </div>
        </header>

        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                style={{
                  padding: '0.5rem 0.9rem',
                  borderRadius: 6,
                  textDecoration: 'none',
                  color: tab.id === 'users' ? '#fff' : '#9ca3af',
                  background: tab.id === 'users' ? '#333' : 'transparent',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ margin: 0 }}>Users</h1>
            <button onClick={fetchUsers} disabled={fetching} style={{ padding: '0.5rem 1rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: fetching ? 'not-allowed' : 'pointer' }}>
              {fetching ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by email, phone, or UID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '0.6rem 1rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', minWidth: 250, flex: 1 }}
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ padding: '0.6rem 1rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
            >
              <option value="">All Roles</option>
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>

          <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1rem', marginBottom: '1.5rem' }}>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
              {roleFilter && ` with role: ${roleFilter}`}
            </p>
          </div>

          <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Logins</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Last Active</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Created</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                      {fetching ? 'Loading users...' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.uid}>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #222' }}>
                        <div style={{ fontWeight: 500 }}>{user.email || 'No email'}</div>
                        {user.phone && <div style={{ fontSize: '0.75rem', color: '#666' }}>{user.phone}</div>}
                        <div style={{ fontSize: '0.7rem', color: '#444', fontFamily: 'monospace' }}>{user.uid}</div>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>
                        <span style={{ background: getRoleBadgeColor(user.role), color: '#fff', padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.75rem' }}>
                          {user.role || 'invitee'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>{user.loginCount || 0}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #222', color: '#9ca3af' }}>{formatDate(user.lastLoginAt)}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #222', color: '#9ca3af' }}>{formatDate(user.createdAt)}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>
                        <button
                          onClick={() => { setSelectedUser(user); setEditingUser(true); }}
                          style={{ padding: '0.3rem 0.6rem', background: '#333', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {editingUser && selectedUser && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '2rem', maxWidth: 500, width: '90%' }}>
                <h2 style={{ marginTop: 0 }}>Edit User Role</h2>
                <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
                  {selectedUser.email || selectedUser.phone || selectedUser.uid}
                </p>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Role</label>
                  <select
                    value={selectedUser.role || 'invitee'}
                    onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>{role.label} - {role.description}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={() => { setSelectedUser(null); setEditingUser(false); }}
                    style={{ flex: 1, padding: '0.75rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUpdateRole(selectedUser.uid, selectedUser.role)}
                    style={{ flex: 1, padding: '0.75rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default AdminUsers;